import { useState } from "react";
import { FiPhone, FiMail, FiGlobe, FiMapPin } from "react-icons/fi";

import FallbackImage from "components/FallbackImage/FallbackImage";
import AccordionLargeTextDisplay from "newui/components/AccordionLargeTextDisplay";
import { Modal } from "newui/components/ui/Modal";
import TextBoxHTML from "newui/components/TextBoxHTML";
import SkeletonBase from "newui/components/Skeleton";

import type { Company } from "newui/features/Company/types";
import { ServiceabilityLocationDisplay } from "../../CompanyServiceability";

interface CompanySidebarProps {
	company: Company;
	isLoading: boolean;
}

const ContactItem: React.FC<{
	href?: string;
	icon: React.ReactNode;
	isLink?: boolean;
	value: string | null | undefined;
}> = ({ icon, value, href, isLink = false }) => {
	if (!value) return null;

	if (href || isLink) {
		return (
			<a
				href={href}
				target={href?.startsWith("http") ? "_blank" : undefined}
				rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
				className="flex items-start gap-2 text-sm text-blue-600 underline decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-800 hover:decoration-blue-600"
			>
				<span className="mt-0.5 shrink-0">{icon}</span>
				<span className="break-all">{value}</span>
			</a>
		);
	}

	return (
		<div className="flex items-start gap-2 text-sm text-gray-600">
			<span className="mt-0.5 shrink-0 text-gray-400">{icon}</span>
			<span className="break-all">{value}</span>
		</div>
	);
};

export const CompanySidebar: React.FC<CompanySidebarProps> = ({
	company,
	isLoading,
}) => {
	const [aboutModalOpen, setAboutModalOpen] = useState(false);

	const globalPresence = company.global_presence?.map((g) => g.name) ?? [];
	const primaryAddress = company.locations?.[0]?.address;

	if (isLoading) {
		return (
			<div className="hidden w-[350px] shrink-0 flex-col gap-4 border-r border-gray-200 p-6 lg:flex xl:w-[400px]">
				<SkeletonBase width="80px" height="80px" />
				<SkeletonBase width="200px" height="24px" />
				<SkeletonBase width="100%" height="48px" />
				<SkeletonBase width="100%" height="80px" />
			</div>
		);
	}

	return (
		<aside className="hidden w-[350px] shrink-0 flex-col border-r border-gray-200 lg:flex xl:w-[400px]">
			<div className="sticky top-0 flex flex-col gap-4 overflow-y-auto p-6">
				{/* Logo */}
				<div className="h-20 w-20 overflow-hidden rounded-lg border border-gray-100">
					<FallbackImage
						src={company.logo_path}
						alt={company.name}
						className="h-full w-full object-contain"
					/>
				</div>

				{/* Name */}
				<h1 className="font-roboto text-xl font-semibold text-gray-900">
					{company.name}
				</h1>

				{/* Description */}
				<AccordionLargeTextDisplay
					text={company.description}
					title="About"
					maxTextDisplay={120}
					isLoading={false}
				/>

				{/* Company Metadata */}
				{(company.years_trading || globalPresence.length > 0) && (
					<div className="flex flex-col gap-1 border-t border-gray-100 pt-4">
						<h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
							Company Information
						</h3>
						{company.years_trading && (
							<p className="text-sm text-gray-600">
								Years of Trading:{" "}
								<span className="font-medium text-gray-900">
									{company.years_trading}
								</span>
							</p>
						)}
						{globalPresence.length > 0 && (
							<p className="text-sm text-gray-600">
								Global Presence:{" "}
								<span className="font-medium text-gray-900">
									{globalPresence.join(", ")}
								</span>
							</p>
						)}
						<ServiceabilityLocationDisplay value={company.serviceability_location_ids} showCode />
					</div>
				)}

				{/* Contact Details */}
				<div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
					<h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
						Contact Information
					</h3>
					<ContactItem
						icon={<FiPhone size={14} />}
						value={company.phone}
						href={company.phone ? `tel:${company.phone}` : undefined}
						isLink
					/>
					<ContactItem
						icon={<FiMail size={14} />}
						value={company.email}
						href={company.email ? `mailto:${company.email}` : undefined}
						isLink
					/>
					<ContactItem
						icon={<FiMapPin size={14} />}
						value={primaryAddress}
					/>
					<ContactItem
						icon={<FiGlobe size={14} />}
						value={company.website}
						href={
							company.website
								? company.website.startsWith("http")
									? company.website
									: `https://${company.website}`
								: undefined
						}
						isLink
					/>
				</div>
			</div>

			{/* About Modal */}
			<Modal
				isOpen={aboutModalOpen}
				onClose={() => setAboutModalOpen(false)}
				title={company.name}
				size="xl"
			>
				{company.banner_path && (
					<img
						src={company.banner_path}
						alt={`${company.name} banner`}
						className="mb-4 h-48 w-full rounded-lg object-cover"
					/>
				)}
				<TextBoxHTML html={company.description} />
			</Modal>
		</aside>
	);
};
